import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { Mail, CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { requestMagicLink } from '@/api/auth.api';
import { useAuth } from '@/hooks/useAuth';

type Status =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'sent'; email: string; devToken: string | null }
  | { kind: 'error'; message: string };

/**
 * Real login flow. Submits the email; the server emails a magic link (or
 * returns a devToken in MOCK_AUTH mode); the user clicks the link, lands at
 * /auth/authenticate?token=…, which exchanges the token for a session cookie.
 *
 * If already authenticated, redirects to /dashboard.
 */
export function LoginPage() {
  const { state } = useAuth();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  if (state.status === 'authenticated') {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setStatus({ kind: 'submitting' });
    try {
      const result = await requestMagicLink(email);
      setStatus({ kind: 'sent', email, devToken: result.devToken ?? null });
    } catch {
      // Server-side this returns 200 on most failure modes, so this catch
      // is only network-level errors. Surface them generically.
      setStatus({ kind: 'error', message: 'Could not reach the server. Try again in a moment.' });
    }
  }

  // ── "Email sent" success state ──
  if (status.kind === 'sent') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-kw-bg-blue p-4">
        <Card className="w-full max-w-sm p-6 sm:p-8">
          <div className="mb-6 flex flex-col items-center gap-3">
            <div className="grid size-12 place-items-center rounded-kw-lg bg-kw-emerald-light text-kw-tag-text">
              <CheckCircle2 className="size-6" />
            </div>
            <div className="text-center">
              <h1 className="typo-h2">Check your email</h1>
              <p className="mt-1 text-[13px] text-kw-muted">
                If <strong className="text-kw-shark">{status.email}</strong> matches an account, we just sent
                a sign-in link.
              </p>
            </div>
          </div>

          {status.devToken ? (
            <div className="rounded-kw-md border border-dashed border-kw-warning-text/40 bg-kw-warning-bg p-3">
              <p className="text-[12px] font-medium text-kw-warning-text">
                Dev mode — magic link doesn&apos;t actually email.
              </p>
              <p className="mt-1 text-[11px] text-kw-warning-text/80">
                Open the verify page with this token:
              </p>
              <a
                href={`/auth/authenticate?token=${encodeURIComponent(status.devToken)}`}
                className="mt-2 block break-all rounded bg-white px-2 py-1.5 font-mono text-[11px] text-kw-shark hover:bg-kw-bg"
              >
                /auth/authenticate?token={status.devToken}
              </a>
            </div>
          ) : null}

          <Button
            variant="ghost"
            size="sm"
            className="mt-6 w-full"
            onClick={() => {
              setStatus({ kind: 'idle' });
            }}
          >
            Use a different email
          </Button>
        </Card>
      </div>
    );
  }

  // ── Form state ──
  return (
    <div className="flex min-h-screen items-center justify-center bg-kw-bg-blue p-4">
      <Card className="w-full max-w-sm p-6 sm:p-8">
        <div className="mb-6 flex flex-col items-center gap-3">
          <div className="grid size-12 place-items-center rounded-kw-lg bg-kw-cerulean text-[20px] font-bold text-white">
            K
          </div>
          <div className="text-center">
            <h1 className="typo-h2">Kaerwell Staff Portal</h1>
            <p className="mt-1 text-[13px] text-kw-muted">Sign in with your work email.</p>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="space-y-4"
        >
          <Input
            label="Work email"
            name="email"
            type="email"
            autoComplete="email"
            required
            placeholder="you@kaerwell.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            error={status.kind === 'error' ? status.message : undefined}
          />
          <Button type="submit" size="lg" className="w-full" isLoading={status.kind === 'submitting'}>
            <Mail className="size-4" />
            Send magic link
          </Button>
        </form>

        <p className="mt-6 text-center text-[12px] text-kw-muted">
          We&apos;ll email you a one-time link to sign in. No password required.
        </p>
      </Card>
    </div>
  );
}
